import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Circle, ChevronRight, PlayCircle, FileText, Compass, GraduationCap } from "lucide-react";
import { api, errMsg, useFetch } from "../lib/api";
import { mediaUrl } from "../lib/upload";
import { PageHeader, Section, Loading } from "../components/bits";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { useApp } from "../context/AppContext";

export function Progress({ done, total, testid = "training-progress" }) {
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <div data-testid={testid}>
      <div className="mb-1.5 flex justify-between text-sm"><span className="font-semibold text-slate-700">{done} / {total} module(s) terminé(s)</span><span className="font-bold text-teal-700">{pct} %</span></div>
      <div className="h-2.5 overflow-hidden rounded-full bg-slate-200"><div className="h-full rounded-full bg-teal-600 transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

export default function Help() {
  const { data } = useFetch("/training");
  const { reloadMe } = useApp();
  const nav = useNavigate();
  const relaunch = async () => { await api.put("/auth/tour", { done: false }); await reloadMe(); nav("/"); };
  return (
    <div data-testid="help-page">
      <PageHeader title="Centre d'aide et de formation" subtitle="Apprenez à utiliser AVELIS à votre rythme." back="/" />
      {!data ? <Loading /> : (
        <>
          <div className="card mb-6 p-5 fade-up"><Progress done={data.done} total={data.total} /></div>
          <div className="space-y-2 fade-up">
            {data.modules.map((m, i) => (
              <Link key={m.id} to={`/aide/${m.id}`} className="card tap flex items-center gap-4 px-4 py-4" data-testid={`training-module-${i + 1}`}>
                {m.done ? <CheckCircle2 className="h-6 w-6 shrink-0 text-teal-600" /> : <Circle className="h-6 w-6 shrink-0 text-slate-300" />}
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-bold uppercase tracking-wide text-slate-400">Module {i + 1}</div>
                  <div className="font-bold text-slate-900">{m.title}</div>
                  <div className="text-sm text-slate-500">{m.summary}</div>
                </div>
                <ChevronRight className="h-5 w-5 shrink-0 text-slate-300" />
              </Link>
            ))}
            {data.modules.length === 0 && <p className="card p-5 text-slate-500">Aucune formation publiée pour le moment.</p>}
          </div>
          <button onClick={relaunch} className="btn-ghost mt-6 w-full" data-testid="help-relaunch-tour-btn">
            <Compass className="h-5 w-5" /> Revoir la visite guidée
          </button>
          <p className="mt-6 text-center text-sm text-slate-500">Une question ? <Link className="font-semibold text-teal-700" to="/assistance" data-testid="help-contact-support">Contacter l'assistance</Link> · <a className="font-semibold text-teal-700" href="mailto:contact@avelis.be">contact@avelis.be</a></p>
        </>
      )}
    </div>
  );
}

function Video({ src }) {
  if (src) return <video src={mediaUrl(src)} controls playsInline preload="metadata" className="w-full rounded-2xl bg-black" data-testid="module-video" />;
  return (
    <div className="flex aspect-video flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-slate-200 text-slate-400" data-testid="module-video-placeholder">
      <PlayCircle className="h-10 w-10" /><span className="text-sm font-semibold">Vidéo explicative bientôt disponible</span>
    </div>
  );
}

function Steps({ steps }) {
  return (
    <ol className="space-y-4">
      {steps.map((s, i) => (
        <li key={i} className="card overflow-hidden" data-testid={`module-step-${i + 1}`}>
          <div className="flex gap-3 p-4">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-teal-600 text-sm font-bold text-white">{i + 1}</span>
            <div><div className="font-bold text-slate-900">{s.title}</div><p className="mt-1 whitespace-pre-line text-[15px] leading-relaxed text-slate-600">{s.text}</p></div>
          </div>
          {s.image && <div className="bg-slate-100 p-3"><img src={mediaUrl(s.image)} alt={s.title} loading="lazy" className="mx-auto max-h-[460px] rounded-xl border border-slate-200 shadow-sm" /></div>}
        </li>
      ))}
    </ol>
  );
}

export function HelpModule() {
  const { id } = useParams();
  const { data: m, reload } = useFetch(`/training/${id}`);
  if (!m) return <Loading />;
  const toggle = async () => {
    try {
      if (m.done) await api.delete(`/training/${id}/complete`); else { await api.post(`/training/${id}/complete`); toast.success("Bravo, module terminé !"); }
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="help-module-page">
      <PageHeader title={m.title} subtitle={m.summary} back="/aide" />
      <Section title="Vidéo"><Video src={m.video} /></Section>
      <Section title="Tutoriel pas à pas"><Steps steps={m.steps || []} /></Section>
      {m.docs?.length > 0 && (
        <Section title="Documents">
          <div className="space-y-2">{m.docs.map((d, i) => (
            <a key={i} href={mediaUrl(d.url)} target="_blank" rel="noreferrer" className="card tap flex items-center gap-3 px-4 py-3" data-testid={`module-doc-${i}`}><FileText className="h-5 w-5 text-teal-700" /><span className="flex-1 font-semibold">{d.name}</span><ChevronRight className="h-4 w-4 text-slate-300" /></a>
          ))}</div>
        </Section>
      )}
      {m.faq?.length > 0 && (
        <Section title="Questions fréquentes">
          <Accordion type="single" collapsible className="card px-4">
            {m.faq.map((f, i) => (
              <AccordionItem key={i} value={`f${i}`} className="last:border-0">
                <AccordionTrigger className="text-left font-semibold" data-testid={`module-faq-${i}`}>{f.q}</AccordionTrigger>
                <AccordionContent className="text-[15px] text-slate-600">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Section>
      )}
      <button onClick={toggle} className={`${m.done ? "btn-ghost" : "btn-primary"} w-full !min-h-[56px]`} data-testid="module-complete-btn">
        {m.done ? <><CheckCircle2 className="h-5 w-5 text-teal-600" /> Terminé · marquer comme non terminé</> : <><GraduationCap className="h-5 w-5" /> J'ai terminé</>}
      </button>
    </div>
  );
}
